"use client";

import React, { useRef, useEffect, useState, useCallback } from "react";
import { Artifact } from "@/components/create-artifact";
import { useSelector, useDispatch } from "react-redux";
import { LoaderIcon } from "@/components/icons";
import { useIsMobile } from "@/hooks/use-mobile";
import { CodeEditor } from "@/components/code-editor";
import { encryptHtml } from "../../lib/cryptoUtils";
import { setHtmlContentNew, setIsSavedChanges } from "@/store/mainSlice";
import { store, RootState } from "@/store";

export const htmlArtifact = new Artifact<"html", {}>({
  kind: "html",
  description: "Displays the HTML generated by the model.",

  initialize: async () => {
    // No metadata needed
  },

  onStreamPart: async () => {
    // Streaming is handled by MultimodalInput -> artifact.content
  },

  content: ({ content, ...rest }) => {
    const lastHtml = useRef<string | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const iframeRefNew = useRef<HTMLIFrameElement>(null);
    const divRef = useRef(null);
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const htmlContentNew = useSelector(
      (state: RootState) => state.html.htmlContent
    );
    const {
      isTransparentLoading,
      isCodeView,
      userData,
      saveDbStore,
      forStreamingHtml,
      activeTab,
    } = useSelector((state) => state.main);

    // console.log("htmlContentNew", forStreamingHtml);

    useEffect(() => {
      if (content) {
        lastHtml.current = content;
      } else if ((rest as any).constnet) {
        lastHtml.current = (rest as any).constnet;
      }
    }, [content, rest]);

    const htmlContent = content || (rest as any).constnet || lastHtml.current;
    const [htmlForCode, setHtmlForCode] = useState(htmlContentNew);

    const useDebounce = (func: (...args: any[]) => void, delay: number) => {
      const timeoutRef = useRef<NodeJS.Timeout | null>(null);

      return useCallback(
        (...args: any[]) => {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            func(...args);
          }, delay);
        },
        [func, delay]
      );
    };

    const savePromptToDB = async (htmlToSave: string) => {
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: userData._id,
              screen_id: saveDbStore.screen_id,
              html_file_detail: saveDbStore.html_file_detail,
              new_html_data: htmlToSave,
              is_followup: saveDbStore.is_followup,
              creditUsed: saveDbStore.creditUsed,
              isCodeViewChanges: true,
            }),
          }
        );

        const data = await response.json();

        dispatch(setIsSavedChanges(new Date().toISOString()));
        // console.log("Saved successfully:", data);
      } catch (err) {
        console.error("Error saving to DB:", err);
      }
    };

    const debouncedSavePrompt = useDebounce(savePromptToDB, 500);

    useEffect(() => {
      setHtmlForCode(htmlContentNew);
    }, [isTransparentLoading]);

    useEffect(() => {
      const button = document.getElementById("closeHTMLEditor");
      // console.log("setHtmlContentNew-11ss", button);
      if (!button) return;

      const handleClose = () => {
        dispatch(setHtmlContentNew(htmlForCode));
      };

      button.addEventListener("click", handleClose);

      return () => {
        button.removeEventListener("click", handleClose);
      };
    }, [htmlForCode, dispatch]);

    useEffect(() => {
      dispatch(setHtmlContentNew(htmlForCode));
    }, [isCodeView]);

    useEffect(() => {
      const button = document.getElementById("codeModeBtn");
      if (!button) return;

      const handleToggleMode = () => {
        dispatch(setHtmlContentNew(htmlForCode));
      };

      button.addEventListener("click", handleToggleMode);

      return () => {
        button.removeEventListener("click", handleToggleMode);
      };
    }, [htmlForCode, dispatch]);

    const debouncedSaveToServer = useRef<any>();
    useEffect(() => {
      debouncedSaveToServer.current = (content: string) => {
        if (debouncedSaveToServer.current.timer)
          clearTimeout(debouncedSaveToServer.current.timer);
        debouncedSaveToServer.current.timer = setTimeout(
          () => savePromptToDB(content),
          800
        );
      };
    }, []);

    function isSafariBrowser() {
      const ua = navigator.userAgent;
      return /^((?!chrome|android).)*safari/i.test(ua);
    }

useEffect(() => {
  if (!htmlForCode || !iframeRef.current) return;
  // console.log("kkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkk", htmlForCode);

  try {
    // 👇 Inject style tag into <head> before rendering
    const updatedHtml = htmlForCode.replace(
      "</head>",
      `<style>
        a {
          pointer-events: none !important;
          cursor: default !important;
          text-decoration: none !important;
          color: inherit !important;
        }
      </style></head>`
    );

    if (isSafariBrowser()) {
      if ("srcdoc" in iframeRef.current) {
        iframeRef.current.srcdoc = updatedHtml;
      } else {
        const blob = new Blob([updatedHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        iframeRef.current.src = url;
        return () => URL.revokeObjectURL(url);
      }
    } else {
      const blob = new Blob([updatedHtml], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      iframeRef.current.src = url;
      return () => URL.revokeObjectURL(url);
    }
  } catch (err) {
    console.error("Iframe load error:", err);
  }
}, [htmlForCode, isCodeView]);

    useEffect(() => {
      if (!forStreamingHtml || !iframeRefNew.current) return;
      try {
        if (isSafariBrowser()) {
          if ("srcdoc" in iframeRefNew.current) {
            iframeRefNew.current.srcdoc = forStreamingHtml;
          } else {
            const blob = new Blob([forStreamingHtml], { type: "text/html" });
            const url = URL.createObjectURL(blob);
            iframeRefNew.current.src = url;
            return () => URL.revokeObjectURL(url);
          }
        } else {
          const blob = new Blob([forStreamingHtml], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          iframeRefNew.current.src = url;
          return () => URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error("Iframe load error:", err);
      }
    }, [forStreamingHtml]);

    // Auto scroll to bottom whenever htmlContent changes
    useEffect(() => {
      if (divRef.current) {
        divRef.current.scrollTop = divRef.current.scrollHeight;
      }
    }, [htmlContent, forStreamingHtml]);

    let waitingText = "Waiting for HTML...";

    if (activeTab === "Page Builder") {
      waitingText = "Waiting for web page...";
    } else if (activeTab === "Card Maker") {
      waitingText = "Waiting for card...";
    }
    if (forStreamingHtml == "" && htmlContentNew == null) {
      return (
        <div className="flex flex-col h-full w-full">
          <div className="flex flex-col flex-1 items-center justify-center">
            <div className="animate-spin h-[50px] w-[50px]">
              <LoaderIcon size={50} />
            </div>
            <div className="p-4">{waitingText}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-full overflow-auto border rounded-md relative inner_card_box">
        {isTransparentLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-sm">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}

        {isCodeView && (
          <CodeEditor
            content={htmlContentNew}
            status="idle"
            onSaveContent={(newContent: any) => {
              setHtmlForCode(newContent); // update local state immediately
              debouncedSavePrompt(newContent); // save latest content to server
            }}
          />
        )}

        {!isCodeView && htmlContentNew !== null && (
          <iframe
            ref={iframeRef}
            data-hj-allow-iframe
            title="HTML Preview"
            className="w-full h-full"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
        )}

        {!isCodeView && htmlContentNew == null && (
          <iframe
            ref={iframeRefNew}
            data-hj-allow-iframe
            title="HTML Preview"
            className="w-full h-full"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
        )}
      </div>
    );
  },

  actions: [],
  toolbar: [],
});
